import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CustomerFormSubmissions } from '../../store/slices';
import { FilterBar, useFilterBarStyles } from '../ui';
import { dateParam } from '../../lib/orderFilters';

const SORT_DIRECTIONS = [
    { value: 'DESC', label: 'Newest first' },
    { value: 'ASC', label: 'Oldest first' },
];

const PROCESSED_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Processed' },
    { value: 'false', label: 'Unprocessed' },
];

export const DEFAULT_SUBMISSION_PARAMS = {
    page: 1,
    limit: 50,
    sortDirection: 'DESC',
    typeId: null,
    email: null,
    processed: null,
    createdAtMin: null,
    createdAtMax: null,
};

const EMAIL_DEBOUNCE = 500;

interface Props {
    queryParams: any;
    loadSubmissions: (params: any) => void;
    loading: boolean;
    types: { id: number; label: string }[];
}

/** Email, form type, processed, date range and sort; every change requests page 1. */
export default function CustomerFormSubmissionsFilters({ queryParams, loadSubmissions, loading, types }: Props) {
    const dispatch = useDispatch();
    const classes = useFilterBarStyles();
    const [email, setEmail] = useState<string>(queryParams?.email || '');
    const emailTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setEmail(queryParams?.email || '');
    }, [queryParams?.email]);

    useEffect(() => () => {
        if (emailTimer.current) clearTimeout(emailTimer.current);
    }, []);

    const handleFilterChange = (name: string, value: any) => {
        loadSubmissions({ ...queryParams, page: 1, [name]: value });
    };

    const onEmail = (value: string) => {
        setEmail(value);
        if (emailTimer.current) clearTimeout(emailTimer.current);
        emailTimer.current = setTimeout(() => handleFilterChange('email', value.trim() || null), EMAIL_DEBOUNCE);
    };

    const processedValue = queryParams?.processed === true ? 'true' : queryParams?.processed === false ? 'false' : '';

    const reset = () => {
        if (emailTimer.current) clearTimeout(emailTimer.current);
        dispatch(CustomerFormSubmissions.clearCustomerFormSubmissionsFilters());
        loadSubmissions({ ...DEFAULT_SUBMISSION_PARAMS });
    };

    return (
        <FilterBar label="Form submission filters">
            <TextField
                className={classes.search}
                size="small"
                label="Email"
                type="search"
                value={email}
                disabled={loading}
                onChange={(event) => onEmail(event.target.value)}
            />
            <FormControl size="small" className={classes.select}>
                <InputLabel id="submission-type-label">Form type</InputLabel>
                <Select
                    labelId="submission-type-label"
                    label="Form type"
                    value={queryParams?.typeId ?? ''}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('typeId', event.target.value === '' ? null : event.target.value)}
                >
                    <MenuItem value="">All</MenuItem>
                    {types.map((type) => (
                        <MenuItem key={type.id} value={type.id}>{type.label || `Type #${type.id}`}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" className={classes.select}>
                <InputLabel id="submission-processed-label">Processed</InputLabel>
                <Select
                    labelId="submission-processed-label"
                    label="Processed"
                    value={processedValue}
                    disabled={loading}
                    onChange={(event) => {
                        const value = event.target.value as string;
                        handleFilterChange('processed', value === '' ? null : value === 'true');
                    }}
                >
                    {PROCESSED_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={queryParams?.createdAtMin ? Dayjs(queryParams.createdAtMin) : null}
                    maxDate={queryParams?.createdAtMax ? Dayjs(queryParams.createdAtMax) : Dayjs(new Date())}
                    disabled={loading}
                    onChange={(value) => handleFilterChange('createdAtMin', value ? dateParam(value) : null)}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={queryParams?.createdAtMax ? Dayjs(queryParams.createdAtMax) : null}
                    maxDate={Dayjs(new Date())}
                    disabled={loading}
                    onChange={(value) => handleFilterChange('createdAtMax', value ? dateParam(value) : null)}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <FormControl size="small" className={classes.select}>
                <InputLabel id="submission-sort-label">Sort</InputLabel>
                <Select
                    labelId="submission-sort-label"
                    label="Sort"
                    value={queryParams?.sortDirection || 'DESC'}
                    disabled={loading}
                    onChange={(event) => handleFilterChange('sortDirection', event.target.value)}
                >
                    {SORT_DIRECTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button className={classes.clear} variant="text" size="small" onClick={reset} disabled={loading}>
                Reset
            </Button>
        </FilterBar>
    );
}
